// ========================================
// pages/TrusteeActions.js - Trustee Actions Page
// ========================================
// PURPOSE: Allow users to manage all users where they are a trustee
// - View list of users (cards on mobile, rows on desktop)
// - See status (Active, Dormant, Deceased, Pending)
// - Request early verification for active/dormant users
// - Resend or cancel pending invitations
// - Educational info box about trustee role
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import Footer from '../components/Footer';

const TrusteeActions = () => {
  const navigate = useNavigate();
  
  // State variables
  const [trusteeRelations, setTrusteeRelations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  // ========================================
  // FETCH ALL TRUSTEE RELATIONSHIPS
  // ========================================
  useEffect(() => {
    fetchTrusteeRelations();
  }, []);

  const fetchTrusteeRelations = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/trustees/relationships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setTrusteeRelations(response.data);
    } catch (err) {
      console.error('Error fetching trustee relationships:', err);
      setError('Failed to load trustee relationships');
      setTrusteeRelations([]);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // REQUEST EARLY VERIFICATION
  // ========================================
  const requestEarlyVerification = async (user) => {
    setSelectedUser(user);
    setShowConfirmModal(true);
  };

  const confirmEarlyVerification = async () => {
    if (!selectedUser) return;
    
    setActionInProgress(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/trustee/early-verification`,
        { userId: selectedUser.id },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      alert(`Early verification requested for ${selectedUser.fullName}. They have 21 days to object.`);
      setShowConfirmModal(false);
      setSelectedUser(null);
      fetchTrusteeRelations(); // Refresh the list
      
    } catch (err) {
      console.error('Error requesting early verification:', err);
      alert(err.response?.data?.error || 'Failed to request early verification');
    } finally {
      setActionInProgress(false);
    }
  };

  const cancelEarlyVerification = () => {
    setShowConfirmModal(false);
    setSelectedUser(null);
  };

  // ========================================
  // RESEND INVITATION
  // ========================================
  const resendInvitation = async (trusteeId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/trustees/${trusteeId}/resend`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      alert('Invitation resent successfully');
      fetchTrusteeRelations();
      
    } catch (err) {
      console.error('Error resending invitation:', err);
      alert(err.response?.data?.error || 'Failed to resend invitation');
    }
  };

  // ========================================
  // CANCEL PENDING INVITATION
  // ========================================
  const cancelInvitation = async (trusteeId, userName) => {
    if (!window.confirm(`Are you sure you want to cancel the invitation for ${userName}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/trustees/${trusteeId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      alert('Invitation cancelled successfully');
      fetchTrusteeRelations();
      
    } catch (err) {
      console.error('Error cancelling invitation:', err);
      alert(err.response?.data?.error || 'Failed to cancel invitation');
    }
  };

  // ========================================
  // HELPER: GET STATUS BADGE CLASS
  // ========================================
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'trustee-status-active';
      case 'dormant':
        return 'trustee-status-dormant';
      case 'deceased':
        return 'trustee-status-deceased';
      case 'pending':
        return 'trustee-status-pending';
      case 'early_verification_pending':
        return 'trustee-status-warning';
      default:
        return 'trustee-status-default';
    }
  };

  // ========================================
  // HELPER: GET STATUS LABEL
  // ========================================
  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'dormant':
        return 'Dormant';
      case 'deceased':
        return 'Deceased';
      case 'pending':
        return 'Pending Invitation';
      case 'early_verification_pending':
        return 'Early Verification Pending';
      default:
        return 'Unknown';
    }
  };

  // ========================================
  // HELPER: GET DAYS REMAINING COLOR
  // ========================================
  const getDaysColorClass = (days) => {
    if (days <= 30) return 'days-green';
    if (days <= 60) return 'days-yellow';
    if (days <= 89) return 'days-orange';
    return 'days-red';
  };

  // ========================================
  // FORMAT DATE
  // ========================================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // ========================================
  // CALCULATE DAYS SINCE LAST CHECK-IN
  // ========================================
  const getDaysSinceLastCheckIn = (lastCheckIn) => {
    if (!lastCheckIn) return null;
    const last = new Date(lastCheckIn);
    const now = new Date();
    const diffTime = Math.abs(now - last);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // ========================================
  // RENDER LOADING STATE
  // ========================================
  if (loading) {
    return (
      <div className="trustee-actions-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading trustee relationships...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trustee-actions-container">
      
      {/* Header */}
      <div className="trustee-actions-header">
        <div className="trustee-actions-header-inner">
          <h1 className="trustee-actions-title">Trustee Actions</h1>
          <Link to="/dashboard" className="trustee-actions-back">
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="trustee-actions-content">
        
        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}
        
        {/* Trustee List */}
        {trusteeRelations.length === 0 ? (
          <div className="trustee-empty-state">
            <img 
              src="/images/dashboard/empty-state.png" 
              alt="No trustee relationships" 
              className="trustee-empty-image"
            />
            <h3 className="trustee-empty-title">You are not a trustee for anyone yet</h3>
            <p className="trustee-empty-text">
              When someone adds you as a trustee, they will appear here.
              You will receive an email invitation to confirm.
            </p>
          </div>
        ) : (
          <div className="trustee-list">
            {trusteeRelations.map((relation) => {
              const daysSince = getDaysSinceLastCheckIn(relation.lastCheckIn);
              const daysRemaining = daysSince ? Math.max(0, 90 - daysSince) : null;
              
              return (
                <div key={relation.id} className="trustee-card">
                  <div className="trustee-card-header">
                    <div className="trustee-user-info">
                      <div className="trustee-user-avatar">
                        <img src="/images/profile/user-avatar.png" alt="" />
                      </div>
                      <div className="trustee-user-details">
                        <h3 className="trustee-user-name">{relation.fullName}</h3>
                        <p className="trustee-user-email">{relation.email}</p>
                      </div>
                    </div>
                    <span className={`trustee-status-badge ${getStatusBadgeClass(relation.status)}`}>
                      {getStatusLabel(relation.status)}
                    </span>
                  </div>
                  
                  <div className="trustee-card-body">
                    {/* Last Activity */}
                    <div className="trustee-info-row">
                      <span className="trustee-info-icon">📅</span>
                      <span className="trustee-info-label">Last active:</span>
                      <span className="trustee-info-value">
                        {formatDate(relation.lastCheckIn)}
                        {daysSince !== null && (
                          <span className={`trustee-days-ago ${getDaysColorClass(daysSince)}`}>
                            {' '}({daysSince} days ago)
                          </span>
                        )}
                      </span>
                    </div>
                    
                    {/* Days Remaining (only for active/dormant) */}
                    {(relation.status === 'active' || relation.status === 'dormant') && daysRemaining !== null && (
                      <div className="trustee-info-row">
                        <span className="trustee-info-icon">⏳</span>
                        <span className="trustee-info-label">Days until dormancy:</span>
                        <span className={`trustee-info-value ${getDaysColorClass(daysSince)}`}>
                          {daysRemaining} days remaining
                        </span>
                      </div>
                    )}
                    
                    {/* Trustee Since */}
                    <div className="trustee-info-row">
                      <span className="trustee-info-icon">⚖️</span>
                      <span className="trustee-info-label">Trustee since:</span>
                      <span className="trustee-info-value">{formatDate(relation.trusteeSince)}</span>
                    </div>
                  </div>
                  
                  <div className="trustee-card-actions">
                    {/* Early Verification Button (active/dormant only, not deceased, not already requested) */}
                    {(relation.status === 'active' || relation.status === 'dormant') && (
                      <button
                        onClick={() => requestEarlyVerification(relation)}
                        className="trustee-btn-early-verify"
                        disabled={relation.status === 'early_verification_pending'}
                      >
                        ⚡ Request Early Verification
                      </button>
                    )}
                    
                    {/* Resend Invite Button (pending only) */}
                    {relation.status === 'pending' && (
                      <button
                        onClick={() => resendInvitation(relation.trusteeId)}
                        className="trustee-btn-resend"
                      >
                        📧 Resend Invite
                      </button>
                    )}
                    
                    {/* Cancel Invitation Button (pending only) */}
                    {relation.status === 'pending' && (
                      <button
                        onClick={() => cancelInvitation(relation.trusteeId, relation.fullName)}
                        className="trustee-btn-cancel"
                      >
                        ✕ Cancel Invitation
                      </button>
                    )}
                    
                    {/* No actions for deceased */}
                    {relation.status === 'deceased' && (
                      <span className="trustee-no-actions">No actions available</span>
                    )}
                    
                    {/* Pending early verification message */}
                    {relation.status === 'early_verification_pending' && (
                      <span className="trustee-pending-message">
                        ⏳ Early verification requested. User has 21 days to object.
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Educational Info Box */}
        <div className="trustee-info-box">
          <div className="trustee-info-box-header">
            <span className="trustee-info-box-icon">ℹ️</span>
            <h3 className="trustee-info-box-title">Understanding Your Role as a Trustee</h3>
          </div>
          
          <div className="trustee-info-box-sections">
            {/* What is a Trustee */}
            <div className="trustee-info-section">
              <h4>📋 What is a Trustee?</h4>
              <p>
                As a trustee, you are someone trusted to confirm the death of a Agyapadie Vault user.
                You help ensure their assets are released to the right beneficiaries when they pass away.
              </p>
            </div>
            
            {/* How Death Verification Works */}
            <div className="trustee-info-section">
              <h4>⚙️ How Death Verification Works</h4>
              <ol>
                <li>User misses check-in for 90 days</li>
                <li>System notifies all trustees via email</li>
                <li>Each trustee clicks the link and confirms "deceased"</li>
                <li>When the verification threshold is met, user is marked deceased</li>
                <li>Assets are released to beneficiaries</li>
              </ol>
            </div>
            
            {/* Early Verification */}
            <div className="trustee-info-section highlight">
              <h4>⚡ Early Verification</h4>
              <p>
                You can request early verification at ANY time if you are certain the user has passed away.
              </p>
              <ul>
                <li>User receives email notification</li>
                <li>User has 21 days to object by clicking "I'm Alive"</li>
                <li>Reminder emails sent every 7 days</li>
                <li>After 21 days with no response, user is marked deceased</li>
              </ul>
            </div>
            
            {/* Your Rights & Limitations */}
            <div className="trustee-info-section">
              <h4>🔒 Your Rights & Limitations</h4>
              <div className="trustee-info-two-columns">
                <div>
                  <p className="trustee-info-check">✅ You CAN:</p>
                  <ul>
                    <li>Request early verification</li>
                    <li>View user's status and last activity</li>
                    <li>Cancel pending invitations</li>
                  </ul>
                </div>
                <div>
                  <p className="trustee-info-cross">❌ You CANNOT:</p>
                  <ul>
                    <li>View user's assets or personal information</li>
                    <li>Remove yourself as trustee once accepted</li>
                    <li>Change beneficiary assignments</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Notification Timeline */}
            <div className="trustee-info-section">
              <h4>📧 Notification Timeline</h4>
              <div className="trustee-timeline">
                <div className="trustee-timeline-item">
                  <span className="trustee-timeline-day">Day 0</span>
                  <span className="trustee-timeline-desc">Initial email to user</span>
                </div>
                <div className="trustee-timeline-item">
                  <span className="trustee-timeline-day">Day 7</span>
                  <span className="trustee-timeline-desc">First reminder email</span>
                </div>
                <div className="trustee-timeline-item">
                  <span className="trustee-timeline-day">Day 14</span>
                  <span className="trustee-timeline-desc">Second reminder email</span>
                </div>
                <div className="trustee-timeline-item">
                  <span className="trustee-timeline-day">Day 21</span>
                  <span className="trustee-timeline-desc">Final reminder email</span>
                </div>
                <div className="trustee-timeline-item">
                  <span className="trustee-timeline-day">Day 22</span>
                  <span className="trustee-timeline-desc">User marked deceased (if no objection)</span>
                </div>
              </div>
            </div>
            
            {/* FAQ */}
            <div className="trustee-info-section">
              <h4>❓ Frequently Asked Questions</h4>
              <div className="trustee-faq">
                <div className="trustee-faq-item">
                  <p className="trustee-faq-q">Q: What if I make a mistake?</p>
                  <p className="trustee-faq-a">A: The user has 21 days to object. If they are alive, they can simply click "I'm Alive" to cancel the request.</p>
                </div>
                <div className="trustee-faq-item">
                  <p className="trustee-faq-q">Q: What if the user doesn't respond?</p>
                  <p className="trustee-faq-a">A: After 21 days with no response, the system assumes the user is deceased and proceeds with verification.</p>
                </div>
                <div className="trustee-faq-item">
                  <p className="trustee-faq-q">Q: Can I see the user's assets?</p>
                  <p className="trustee-faq-a">A: No. Trustees only verify death, never see assets. Only beneficiaries see assets after death.</p>
                </div>
                <div className="trustee-faq-item">
                  <p className="trustee-faq-q">Q: What if I no longer want to be a trustee?</p>
                  <p className="trustee-faq-a">A: If you haven't accepted the invitation, you can cancel. If already accepted, only the asset owner can remove you. Contact them directly.</p>
                </div>
              </div>
            </div>
            
            {/* Need Help */}
            <div className="trustee-info-section help">
              <h4>📞 Need Help?</h4>
              <p>Contact support: <a href="mailto:support@agyapadie.com">support@agyapadie.com</a></p>
              <p>or visit our <Link to="/faq">FAQ page</Link></p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
      
      {/* Confirmation Modal for Early Verification */}
      {showConfirmModal && selectedUser && (
        <div className="modal-overlay" onClick={cancelEarlyVerification}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">⚠️</div>
            <h2 className="modal-title">Confirm Early Verification</h2>
            <p className="modal-text">
              You are about to request early verification for <strong>{selectedUser.fullName}</strong>.
            </p>
            <div className="modal-warning">
              <p>• {selectedUser.fullName} will have 21 days to object</p>
              <p>• They will receive emails every 7 days</p>
              <p>• If no response after 21 days, they will be marked deceased</p>
            </div>
            <p className="modal-text">Are you sure you want to proceed?</p>
            <div className="modal-buttons">
              <button onClick={cancelEarlyVerification} className="modal-cancel" disabled={actionInProgress}>
                Cancel
              </button>
              <button onClick={confirmEarlyVerification} className="modal-confirm" disabled={actionInProgress}>
                {actionInProgress ? 'Processing...' : 'Yes, Request Early Verification'}
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default TrusteeActions;